import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Switch, Tooltip } from "@mui/material";
import { DEFAULTPICKERDATE } from "@/types/Entities";
import { replaceDate } from "@/helper/dateHandling";
import { KAER_COMPANY_ID } from "@/helper/systemIds";

interface UseUserTableColumnsOptions {
  companyId?: string;
  companySubjectId?: string;
  subdomain: string;
  canEditUserRoles: boolean;
  onToggleTwoFactor: (event: any, row: any) => void;
}

export const PORTAL_REGISTRATION_EDIT_COLUMNS = [
  {
    accessorKey: "is_portal_registered",
    id: "is_portal_registered",
    hideField: true,
  },
];

export function useUserTableColumns(options: UseUserTableColumnsOptions) {
  const { t } = useTranslation();
  const { subdomain, canEditUserRoles } = options;
  const isAdmin = subdomain === "admin";

  const userColumns = useMemo(() => {
    const columns = [
      {
        header: t("id"),
        accessorKey: "id",
        id: "id",
        hideField: true,
        enableGlobalFilter: false,
        visibleInShowHideMenu: !options.companySubjectId ? false : true,
      },

      {
        header: t("first_name"),
        accessorKey: "first_name",
        id: "first_name",
        enableForCreate: true,
        required: true,
        accessorFn: (row: any) => {
          if (row.is_deleted) {
            return `${row.first_name} (${t("deleted")})`;
          } else {
            return row.first_name;
          }
        },
      },
      {
        header: t("last_name"),
        accessorKey: "last_name",
        id: "last_name",
        required: true,
        enableForCreate: true,
        accessorFn: (row: any) => {
          if (row.is_deleted) {
            return `${row.last_name} (${t("deleted")})`;
          } else {
            return row.last_name;
          }
        },
      },
      {
        header: t("gender"),
        accessorKey: "person.sex",
        required: true,
        errorMessage: t("field_required"),
        childEntityName: "person",
        customColumnFilterId: "person__sex",
        setNoFilterParameter: true,
        filterVariant: "select",
        filterSelectOptions: [
          { value: "male", label: t("male") },
          { value: "female", label: t("female") },
          { value: "diverse", label: t("diverse") },
        ],
        id: "sex",
        enableForCreate: true,
        sortDescFirst: false,
        Cell: ({ cell }: any) => {
          const cellValue = cell.getValue();
          let cellLabel;
          switch (cellValue) {
            case "male":
              cellLabel = t("male");
              break;
            case "female":
              cellLabel = t("female");
              break;
            case "diverse":
              cellLabel = t("diverse");
              break;
            default:
              cellLabel = "-";
          }
          return <span>{cellLabel}</span>;
        },
        selectOptions: [
          {
            value: "male",
            label: t("male"),
          },
          {
            value: "female",
            label: t("female"),
          },
          {
            value: "diverse",
            label: t("diverse"),
          },
        ],
      },

      {
        header: t("email"),
        accessorKey: "email",
        id: "email",
        enableForCreate: true,
        customFilterParameter: "__icontains",
        required: true,
        errorMessage: t("field_required"),
        validate: true,
        errorMessageValidation: t("email_already_in_use"),
      },
      {
        header: t("phone"),
        accessorKey: "person.phone",
        childEntityName: "person",
        customColumnFilterId: "person__phone",
        id: "phone",
        enableForCreate: true,
        customFilterParameter: "_icontains",
      },
      {
        header: t("departments"),
        accessorKey: "department_set",
        customColumnFilterId: "department_name",
        customFilterParameter: "__icontains",
        enableSorting: false,
        id: "department_set",
        accessorFn: (row: any) => {
          const departments: any = [];
          if (row.department_set) {
            for (const key in row.department_set) {
              if (row.department_set[key].name) {
                departments.push(row.department_set[key].name);
              }
            }
          }
          return departments.join(", ");
        },
        enableForCreate: true,
        multiselect: true,
        entity: "department",
      },
      {
        header: t("birth_date"),
        accessorKey: "person.birth_date",
        childEntityName: "person",
        id: "birth_date",
        enableGlobalFilter: false,
        enableForCreate: true,
        datePicker: true,
        required: true,
        errorMessage: t("field_required"),
        validate: true,
        errorMessageValidation: t("birthday_not_today"),
        defaultDate: DEFAULTPICKERDATE.UNSET,
        sortingFn: "datetime",
        filterVariant: "date",
        customColumnFilterId: "person__birth_date",
        setNoFilterParameter: true,
        Cell: ({ cell }: any) => {
          return (
            <span>
              {cell.getValue() === null ? "-" : replaceDate(cell.getValue())}
            </span>
          );
        },
      },
      {
        header: t("analog_user"),
        id: "is_analog",
        enableForCreate: true,
        accessorKey: "is_analog",
        filterVariant: "select",
        filterSelectOptions: [
          { value: "true", label: t("yes") },
          { value: "false", label: t("no") },
        ],
        setNoFilterParameter: true,
        accessorFn: (row: any) => {
          if (row.is_analog) {
            return t("yes");
          } else return t("no");
        },
        selectOptions: [
          { value: false, label: t("no") },
          { value: true, label: t("yes") },
        ],
      },
      {
        header: t("preferred_language"),
        id: "preferred_language",
        childEntityName: "person",
        accessorKey: "person.preferred_language",
        customColumnFilterId: "person__preferred_language",
        enableForCreate: true,
        filterVariant: "select",
        filterSelectOptions: [
          { value: "de", label: t("language_german") },
          { value: "en", label: t("language_english") },
        ],
        selectOptions: [
          { value: "de", label: t("language_german") },
          { value: "en", label: t("language_english") },
        ],
      },
      {
        header: t("worker_id"),
        accessorKey: "person.worker_id",
        childEntityName: "person",
        id: "worker_id",
        customColumnFilterId: "person__worker_id",
        enableForCreate: true,
        enableGlobalFilter: false,
      },
      {
        header: t("user_is_registered"),
        accessorKey: "is_registered",
        id: "is_registered",
        enableForCreate: true,
        customColumnFilterId: "agreed_to_data_collection_on",
        customFilterParameter: "__isnull",
        filterVariant: "select",
        filterSelectOptions: [
          { value: "false", label: t("yes") },
          { value: "true", label: t("no") },
        ],
        hideField: true,
        enableGlobalFilter: false,
        Cell: ({ cell }: any) => {
          return <span>{cell.getValue() ? t("yes") : t("no")}</span>;
        },
      },
      {
        header: t("created_on"),
        accessorKey: "created_on",
        id: "created_on",
        hideField: true,
        visibleInShowHideMenu: true,
        enableGlobalFilter: false,
        filterVariant: "date",
        Cell: ({ cell }: any) => {
          return (
            <span>
              {cell.getValue() === null
                ? "-"
                : replaceDate(cell.getValue(), true)}
            </span>
          );
        },
      },
      {
        header: t("modified_on"),
        accessorKey: "modified_on",
        id: "modified_on",
        hideField: true,
        visibleInShowHideMenu: true,
        enableGlobalFilter: false,
        filterVariant: "date",
        Cell: ({ cell }: any) => {
          return (
            <span>
              {cell.getValue() === null
                ? "-"
                : replaceDate(cell.getValue(), true)}
            </span>
          );
        },
      },
    ];
    let newColumns;
    if (isAdmin === true) {
      newColumns = [
        ...columns.slice(0, columns.length - 2),
        {
          header: t("last_login"),
          accessorKey: "last_login",
          id: "last_login",
          filterVariant: "date",
          enableGlobalFilter: false,
          Cell: ({ cell }: any) => {
            return (
              <span>
                {cell.getValue() === null
                  ? "-"
                  : replaceDate(cell.getValue(), true)}
              </span>
            );
          },
        },
        {
          header: t("is_deleted"),
          accessorKey: "is_deleted",
          id: "is_deleted",
          customColumnFilterId: "is_deleted",
          setNoFilterParameter: true,
          customSortingKey: "is_deleted",
          filterVariant: "select",
          filterSelectOptions: [
            { value: "true", label: t("yes") },
            { value: "false", label: t("no") },
          ],
          accessorFn: (row: any) => {
            if (row.is_deleted) {
              return t("deleted");
            }
          },
        },
        {
          header: t("2fa"),
          id: "requires_two_factor",
          accessorKey: "requires_two_factor",
          customColumnFilterId: "requires_two_factor",
          setNoFilterParameter: true,
          customSortingKey: "requires_two_factor",
          filterVariant: "select",
          filterSelectOptions: [
            { value: "true", label: t("yes") },
            { value: "false", label: t("no") },
          ],
          accessorFn: (row: any) => {
            const isManager = !!row.group_set.find(
              (item: any) => item.name === "manager"
            );

            return (
              <div style={{ display: "flex", justifyContent: "center" }}>
                <Tooltip
                  title={
                    isManager ? t("2fa_cannot_be_toggled_for_managers") : ""
                  }
                  arrow
                  placement="top"
                >
                  <span>
                    <Switch
                      checked={
                        row.requires_two_factor ||
                        row.company_enforced_two_factor
                      }
                      inputProps={{ "aria-label": "controlled" }}
                      onChange={(event: any) => options.onToggleTwoFactor(event, row)}
                      disabled={isManager || row.company_enforced_two_factor}
                    />
                  </span>
                </Tooltip>
              </div>
            );
          },
        },
        ...columns.slice(columns.length - 2),
      ];
    } else newColumns = columns;

    if (options.companyId === KAER_COMPANY_ID) {
      const kaerColumns = [
        ...newColumns.slice(0, 1),
        {
          header: t("title"),
          accessorKey: "person.title",
          childEntityName: "person",
          id: "title",

          enableForCreate: true,
          maxLength: 255,
          maxLengthMessage: t("string_maxLength_error", { number: 255 }),
        },
        ...newColumns.slice(1, 3),
        {
          header: t("highest_group"),
          enableSorting: false,
          accessorKey: "group_set",
          id: "group_set",
          customColumnFilterId: "groups__name",
          customFilterParameter: "__icontains",
          enableForCreate: canEditUserRoles,
          filterVariant: "select",
          filterSelectOptions: [
            { value: "customer", label: t("customer") },
            { value: "manager", label: t("manager") },
          ],

          multiselect: true,
          entity: "group",
          additionalUrlParameters: {
            include_kaer_internal: true,
            assignment_company_id: options.companyId,
          },
          accessorFn: (row: any) => {
            const assignedGroups: any = [];
            if (row.group_set) {
              for (const key in row.group_set) {
                if (row.group_set[key].name) {
                  assignedGroups.push(t(row.group_set[key].name));
                }
              }
            }
            if (subdomain === "admin") {
              return assignedGroups.join(", ");
            }
            if (subdomain === "manage") {
              if (assignedGroups.includes("Manager")) {
                return t("manager");
              } else return t("customer");
            }
          },
        },
        ...newColumns.slice(3, 6),
        {
          header: t("team"),
          accessorKey: "team.name",
          id: "team",

          enableForCreate: true,
          entity: "team",
          accessorFn: (row: any) => {
            if (row.team !== null) {
              return ` ${row.team.name}`;
            } else {
              return "";
            }
          },
        },
        ...newColumns.slice(6, 7),
        {
          header: t("person_additional_description"),
          accessorKey: "person.additional_description",
          childEntityName: "person",
          id: "additional_description",
          enableForCreate: true,
          textarea: true,
        },
        ...newColumns.slice(7),
      ];
      newColumns = kaerColumns;
    } else if (subdomain === "admin") {
      newColumns = [
        ...newColumns.slice(0, 3),
        {
          header: t("groups"),
          accessorKey: "direct_group_set",
          id: "direct_group_set",
          enableForCreate: canEditUserRoles,
          replaceWithAdditionalComponentForModal: true,
          multiselect: true,
          entity: "group",
          additionalUrlParameters: {
            is_additional: true,
            assignment_company_id: options.companyId ?? "",
          },
          accessorFn: (row: any) =>
            (row.group_set || []).map((group: any) => group.name).join(", "),
        },
        ...newColumns.slice(3),
      ];
    }
    return newColumns;
  }, [
    options.companyId,
    options.companySubjectId,
    isAdmin,
    subdomain,
    canEditUserRoles,
    t,
  ]);

  return userColumns;
}
